import { cn } from "@resume-studio/utils";
import * as React from "react";

/**
 * Stripe-style surface primitive. Border, no shadow. Eyebrow label is the
 * 11px uppercase letter-spaced micro-label that sits above the title — used
 * sparingly (KPI groups, "Recent activity" panels) to anchor a section
 * without resorting to oversized headings.
 */
type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  eyebrow?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** Drop the default 20px padding (e.g. when the body is a DataTable). */
  unpadded?: boolean;
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, eyebrow, title, description, actions, unpadded, children, ...props }, ref) => {
    const hasHeader = Boolean(eyebrow || title || description || actions);

    return (
      <div
        ref={ref}
        className={cn("rounded-md border border-border bg-card text-card-foreground", className)}
        {...props}
      >
        {hasHeader && (
          <header
            className={cn(
              "flex items-start justify-between gap-3 border-b border-border px-5 py-4",
            )}
          >
            <div className="min-w-0">
              {eyebrow && (
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
                  {eyebrow}
                </p>
              )}
              {title &&
                (typeof title === "string" ? (
                  <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
                ) : (
                  title
                ))}
              {description && (
                <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
              )}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
          </header>
        )}
        <div className={cn(!unpadded && "p-5")}>{children}</div>
      </div>
    );
  },
);
Card.displayName = "Card";
